<template>
  <article>
    <activity-stage :customRootContainerClass="'bits-about-it'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" id="bits-about-it-content">
        <div :class="['shifty-targets', { tablet: isMobile }]">
          <ul class="text-steps">
            <li :class="`list-step`">
              <color-block-text
                :text="currentRound?.answer"
                :text-step="this.progress.stepIndex"
                :hint-class="getUppercaseHint"
              ></color-block-text>
            </li>
          </ul>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TimelineLite } from 'gsap';
import { createNamespacedHelpers, mapMutations, mapState } from 'vuex';
import ActivityManager from 'mixins/ActivityManager';
import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import ErrorLogger from 'mixins/ErrorLogger';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

const keyboard = createNamespacedHelpers('keyboard');

export default {
  components: {
    ActivityStage,
    ColorBlockText
  },

  mixins: [TypingActivityHelper, ActivityManager, TouchAndHoldHelper, ErrorLogger],

  beforeDestroy() {
    this.setCapsLockSeverity('error');
  },

  data() {
    return {
      activityName: 'ShiftyTargets'
    };
  },

  computed: {
    ...mapState('keyboard', {
      capsLock: state => state.capsLock
    }),
    isMobile() {
      return this.device.isMobile;
    },
    getUppercaseHint() {
      return this.expectedKey &&
        this.expectedKey !== ' ' &&
        this.expectedKey === this.expectedKey.toUpperCase()
        ? 'hint'
        : '';
    }
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLockSeverity']),
    async loadActivity() {
      this.createRounds(this.getActivityState());
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep({ stepCallback: this.stepCallback });
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map(r => ({
          ...r,
          isTouchHold: true,
          shiftKey: this.oneHandedTypingHand ? this.getOneHandedTypingHandShiftKey(r) : r.shiftKey
        })),
        stepIndicator: 'answer'
      };
    },

    getKeyboardConfig() {
      return {
        keys: '  '
      };
    },

    getOneHandedTypingKeyboardKeys() {
      return `caplock${this.currentRound.shiftKey === 'shift1' ? 'shift1' : 'shift2'}`;
    },

    getOneHandedTypingHandShiftKey(round) {
      return round.shiftKey === 'shift1' ? 'shift2' : 'shift1';
    },

    handleCorrectKey(attempt) {
      this.sndType();

      this.$nextTick(() => {
        this.incrementTypingIndex();
        this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence, {
          skipAnnouncement: true
        });
        this.incrementStep();
        this.showHandComponent();

        if (this.progress.stepIndex >= this.currentRound?.answer?.length) {
          this.sndBell();
          this.disableKeyboard();
          this.hideKeyboard();
          this.handleRoundEnd();
        } else {
          this.promptScreenReaderAlert();
        }
      });
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline
          .delay(1)
          .to('.shifty-targets', 0.5, { opacity: 0 })
          .call(this.completeActivity);
      } else {
        this.finishStep();
        timeline
          .to('.text-steps', 0.5, { opacity: 0 })
          .delay(0.5)
          .call(() => this.setupNewRound({ stepCallback: this.stepCallback }))
          .call(this.shouldConfigureKeyboard)
          .call(this.showKeyboard)
          .call(this.enableKeyboard)
          .call(this.showHandComponent)
          .to('.text-steps', 0.5, { opacity: 1 });
      }
    },

    promptScreenReaderAlert() {
      const letter = this.expectedKey;

      let instructions;
      if (letter && letter !== ' ') {
        const shiftKeyLocation = this.currentRound.shiftKey === 'shift1' ? 'left' : 'right';
        instructions =
          letter === letter.toUpperCase()
            ? `Hold the ${shiftKeyLocation} shift key. Then type the letter - ${letter.toLowerCase()}`
            : `Type the letter - ${letter}`;
      }

      if (letter === ' ') {
        instructions = 'Type the space key';
      }

      this.rerenderScreenReaderAlert(instructions);
    },

    shouldConfigureKeyboard() {
      const keys = this.oneHandedTypingHand ? this.getOneHandedTypingKeyboardKeys() : '';
      this.configureKeyboard({
        ...this.$store.state.keyboard.settings,
        keys: `${keys}${this.getKeyboardConfig().keys + this.currentRound.answer}`,
        hideNumberRow: true,
        touchAndHold: true
      });
    },

    showHandComponent() {
      const timeline = new TimelineLite();

      if (this.getUppercaseHint) {
        timeline
          .set('.phoenix-target-hand', { css: { display: 'block' } })
          .set('.active.highlight', { css: { className: '-=highlight' } })
          .set('.hold-key', { css: { className: '-=hide-target' } });
      } else {
        timeline
          .set('.phoenix-target-hand', { css: { display: 'none' } })
          .set('.active', { css: { className: '+=hide-target' } });
      }
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.shouldConfigureKeyboard();
      this.enableKeyboard();

      this.$nextTick(this.setActivityContainerHeight);

      this.errorLoggerStart();
      this.showHandComponent();
    },

    stepCallback() {
      return this.currentRound?.answer;
    },

    toggleKeyboardDisabledState(isDisabled) {
      const phoenixKeyboardWrapperChildDiv = document.querySelector(
        '.phoenix-keyboard-wrapper > div'
      );

      if (
        !phoenixKeyboardWrapperChildDiv ||
        this.currentStep === ' ' ||
        this.expectedKey.toUpperCase() !== this.expectedKey ||
        this.currentStep.toUpperCase() !== this.expectedKey.toUpperCase()
      )
        return;

      this.$nextTick(() => {
        phoenixKeyboardWrapperChildDiv.classList[isDisabled ? 'remove' : 'add']('disabled');
      });
    }
  },
  watch: {
    capsLock(newVal) {
      this.toggleKeyboardDisabledState(newVal);
    },
    currentStep(newVal, oldVal) {
      if (typeof newVal === 'object' || !this.oneHandedTypingHand) return;

      const isLowerCaseChar = newVal.toUpperCase() !== newVal;
      const isSpaceChar = val => val === ' ';

      if (isSpaceChar(oldVal) && !isLowerCaseChar) {
        this.toggleKeyboardDisabledState(this.capsLock);
      }

      if (isSpaceChar(newVal) || isLowerCaseChar) {
        this.setCapsLockSeverity('error');
      } else {
        this.setCapsLockSeverity('alert');
      }
    },
    currentRound(newVal) {
      if (typeof newVal === 'object' && !Object.keys(newVal).length) return;
      if (newVal) this.promptScreenReaderAlert();
    }
  }
};
</script>

<style lang="scss" scoped>
.shifty-targets {
  min-height: 430px;
  display: flex;

  ul {
    list-style-type: none;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    width: 100%;

    @media (max-width: 432px) {
      justify-content: flex-start;
    }

    p {
      font-size: 42px;
      letter-spacing: 20px;
    }
  }

  &.tablet {
    min-height: 300px;
  }
}

.chrome-os {
  .shifty-targets {
    min-height: 300px;
  }
}
</style>
